import { isIOS, isSafari } from 'react-device-detect';
import { dashjs, dashStreamingSettings } from 'utils/loadDash';

/**
 * The scene video shown *behind* a voiceover.
 *
 * Playback runs through the single shared #videoEl so a source switch never needs a
 * fresh user gesture to play with sound (mobile unlocks audio per element). When an
 * action has a voiceover over a video scene, the voiceover owns #videoEl — it has the
 * audio — and the scene video plays here instead, on #bgVideoEl. That element is
 * permanently muted, which is what lets it play() without a gesture on mobile; it
 * must never be unmuted.
 *
 * The voiceover drives: this element plays only while #videoEl is actually playing,
 * and pauses whenever it pauses or stalls. At its own end it holds the last frame,
 * unless the scene config asks it to loop.
 */

export const BG_VIDEO_EL_ID = 'bgVideoEl';
const MAIN_VIDEO_EL_ID = 'videoEl';
// Same tolerance as startVideo: closer than this to the start counts as already there.
const SEEK_EPSILON = 0.1;
// dash encodes often end slightly short of the declared end; treat this close as ended.
const END_EPSILON = 0.05;

export interface IBackgroundClip {
  assetId: string;
  url: string;
  start: number;
  end: number | null;
  loop: boolean;
  // Where the voiceover starts. The voiceover seeking back here (a replay) restarts
  // the background with it, rather than leaving it parked on its last frame.
  leadStart: number;
}

export interface IBackgroundState {
  assetId: string | null;
  ready: boolean;
}

type Listener = (state: IBackgroundState) => void;

class BackgroundVideo {
  private clip: IBackgroundClip | null = null;
  private ready = false;
  // Reached its end and is holding the last frame.
  private held = false;
  // No longer wanted, but kept on its last frame until the slide transition settles.
  private suspended = false;
  private dash: any = null;
  // Bumped per source attach so a superseded async dash load stands down.
  private generation = 0;
  private bound = false;
  private listeners = new Set<Listener>();

  private get el() {
    return document.getElementById(BG_VIDEO_EL_ID) as HTMLVideoElement | null;
  }

  private get main() {
    return document.getElementById(MAIN_VIDEO_EL_ID) as HTMLVideoElement | null;
  }

  get assetId() {
    return this.clip?.assetId ?? null;
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit() {
    const state = { assetId: this.assetId, ready: this.ready };
    this.listeners.forEach(listener => listener(state));
  }

  /** Show `clip`, reusing the attached source when it is the same video. */
  async show(clip: IBackgroundClip) {
    const el = this.el;
    if (!el) return;
    this.bind(el);
    el.muted = true;

    const sameSource = this.clip?.assetId === clip.assetId && !!(el.currentSrc || el.src) && el.readyState > 0;
    this.clip = clip;
    this.held = false;
    this.suspended = false;

    if (sameSource) {
      // A new action on the same scene video: like a shared video scene, only seek.
      if (Math.abs(el.currentTime - clip.start) > SEEK_EPSILON) el.currentTime = clip.start;
      this.sync();
      return;
    }

    this.ready = false;
    this.emit();
    const generation = ++this.generation;
    const url = `${clip.url}#t=${clip.start}`;

    if (isIOS || isSafari) {
      el.src = url;
      el.load();
      return;
    }

    if (!this.dash) {
      const dash = await dashjs();
      if (generation !== this.generation) return;
      this.dash = dash.MediaPlayer().create();
      this.dash.updateSettings(dashStreamingSettings);
      this.dash.initialize(el, null, false);
    }
    if (generation !== this.generation) return;
    try {
      this.dash.attachSource(url);
    } catch (e) {}
  }

  /** Freeze on the current frame, keeping the source (e.g. mid slide transition). */
  pause() {
    this.suspended = true;
    this.el?.pause();
  }

  /** Detach the source entirely. */
  stop() {
    this.generation++;
    const el = this.el;
    this.clip = null;
    this.ready = false;
    this.held = false;
    this.suspended = false;
    if (this.dash) {
      try {
        this.dash.reset();
      } catch (e) {}
      this.dash = null;
    }
    if (el) {
      el.pause();
      el.removeAttribute('src');
      el.load();
    }
    this.emit();
  }

  // Follow the voiceover: play only while it is actually playing.
  private sync = () => {
    const el = this.el;
    const main = this.main;
    if (!el || !this.clip) return;
    const shouldPlay =
      this.ready && !this.held && !this.suspended && !!main && !main.paused && main.readyState >= main.HAVE_FUTURE_DATA;
    if (shouldPlay && el.paused) el.play().catch(() => {});
    else if (!shouldPlay && !el.paused) el.pause();
  };

  private onCanPlay = () => {
    const el = this.el;
    if (!el || !this.clip || this.ready) return;
    if (Math.abs(el.currentTime - this.clip.start) > SEEK_EPSILON) el.currentTime = this.clip.start;
    this.ready = true;
    this.emit();
    this.sync();
  };

  private reachEnd() {
    const el = this.el;
    if (!el || !this.clip) return;
    if (this.clip.loop) {
      el.currentTime = this.clip.start;
      if (el.paused) this.sync();
      return;
    }
    this.held = true;
    el.pause();
  }

  private onTimeUpdate = () => {
    const el = this.el;
    if (!el || !this.clip || this.held || this.clip.end == null) return;
    if (el.currentTime >= this.clip.end - END_EPSILON) this.reachEnd();
  };

  private onEnded = () => {
    if (!this.held) this.reachEnd();
  };

  private onMainSeeked = () => {
    const el = this.el;
    const main = this.main;
    if (!el || !main || !this.clip || !this.ready || this.suspended) return;
    if (Math.abs(main.currentTime - this.clip.leadStart) > SEEK_EPSILON * 2) return;
    this.held = false;
    if (Math.abs(el.currentTime - this.clip.start) > SEEK_EPSILON) el.currentTime = this.clip.start;
    this.sync();
  };

  private bind(el: HTMLVideoElement) {
    if (this.bound) return;
    const main = this.main;
    if (!main) return;
    this.bound = true;

    el.addEventListener('canplay', this.onCanPlay);
    el.addEventListener('loadeddata', this.onCanPlay);
    el.addEventListener('timeupdate', this.onTimeUpdate);
    el.addEventListener('ended', this.onEnded);

    ['play', 'playing', 'pause', 'waiting', 'emptied', 'canplay'].forEach(type =>
      main.addEventListener(type, this.sync),
    );
    main.addEventListener('seeked', this.onMainSeeked);
  }
}

export const backgroundVideo = new BackgroundVideo();
